import { NaturalPersonDto } from 'api/contract';
import { ReferentialKey } from 'api/referential';
import { useAppSelector } from 'app/hooks';
import { Search } from 'lucide-react';
import { CommercialIcon } from 'components/icons/customIcons';
import { PersonSearchBar } from 'features/subscription-process/components/person-search/PersonSearchBar';
import { ExcludeFilter, SearchPersonResult } from 'components/domain/SearchBar/types';
import { useSearchBarSelect } from 'features/subscription-process/components/person-search/useSearchBarSelect';
import { NsKey } from 'enums/NsKey';
import { ActorType } from 'features/new-actor/enums/actorType';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import { selectPendingForm } from 'features/subscription-process/slices/selectors';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCivilityIcon } from 'shared/utils/civility';
import { FormKey } from 'types/form/formKeyEnum';
import { BorrowerInsuredFormValues } from '../borrowerInsuredForm';

interface BorrowerActorSelectorProps {
  isEdit: boolean;
  actorType: ActorType;
  onChange: (type: ActorType) => void;
  excludeIds?: string[];
  /** Civilité live du formulaire (code référentiel) : pilote l'avatar en priorité. */
  currentCivility?: string;
}

const ICON_CLASS = 'w-8 text-primary';

export const BorrowerActorSelector: FC<BorrowerActorSelectorProps> = ({
  actorType,
  isEdit,
  excludeIds = [],
  currentCivility
}) => {
  const { t } = useTranslation(NsKey.ActorForm);
  const { formKey, entity: pendingBorrower } = useAppSelector(
    selectPendingForm()
  );
  const referentialData = useAppSelector(
    selectReferentialDataByKey(ReferentialKey.Civility)
  );
  const { handleSelectNaturalPerson } = useSearchBarSelect();

  // Combine excludeIds with the currently selected person's ID
  const excludeFilter = useMemo<ExcludeFilter<SearchPersonResult> | undefined>(() => {
    const allExcludeIds = [...excludeIds];

    // Add the currently selected person's ID to exclusions
    if (pendingBorrower && 'id' in pendingBorrower && pendingBorrower.id) {
      if (!allExcludeIds.includes(pendingBorrower.id)) {
        allExcludeIds.push(pendingBorrower.id);
      }
    }

    if (allExcludeIds.length === 0) return undefined;
    return {
      field: 'id',
      values: allExcludeIds
    };
  }, [excludeIds, pendingBorrower]);

  const getGenderIcon = useMemo(() => {
    if (actorType === ActorType.BorrowerLegal) {
      return <CommercialIcon className={ICON_CLASS} />;
    }

    // Résolution du code de civilité par ordre de priorité : civilité live du
    // formulaire (dropdown), puis valeurs du formulaire en cours, puis référentiel.
    let civilityCode = currentCivility;

    if (!civilityCode && formKey === FormKey.FormValues && pendingBorrower) {
      civilityCode = (pendingBorrower as BorrowerInsuredFormValues).civility;
    }

    if (!civilityCode && pendingBorrower) {
      const naturalPerson = pendingBorrower as NaturalPersonDto;
      civilityCode = referentialData.find(
        (ref) => ref.id === naturalPerson.civilityId
      )?.code;
    }

    const Icon = getCivilityIcon(civilityCode);
    return <Icon className={ICON_CLASS} />;
  }, [actorType, pendingBorrower, referentialData, formKey, currentCivility]);

  const shouldShowSearchBar = useMemo(
    () => formKey !== FormKey.FormValues,
    [formKey]
  );

  const actorTypeLabel = useMemo(
    () =>
      actorType === ActorType.BorrowerNatural
        ? t('ACTOR_FORM.LABELS.NATURAL_PERSON')
        : t('ACTOR_FORM.LABELS.LEGAL_ENTITY'),
    [t, actorType]
  );

  const containerClass = 'flex w-full justify-between items-center';
  const leftSectionClass = 'flex gap-4 items-center';

  // En édition, la recherche n'a pas lieu d'être mise en avant (on modifie un assuré
  // déjà rattaché) : on garde le champ compact en haut à droite.
  if (isEdit) {
    return (
      <div className={containerClass}>
        <div className={leftSectionClass}>
          {getGenderIcon}
          <p className="text-lg font-bold text-primary">{actorTypeLabel}</p>
        </div>
        {shouldShowSearchBar && (
          <PersonSearchBar
            withNaturalPerson
            withLegalEntity={false}
            onSelect={handleSelectNaturalPerson}
            excludeFilter={excludeFilter}
            size="sm"
            className="w-56"
          />
        )}
      </div>
    );
  }

  // Ajout d'un assuré : la recherche est mise en avant (bloc pleine largeur + accroche)
  // pour inciter à réutiliser un assuré existant plutôt que d'en recréer un doublon.
  return (
    <div className="w-full">
      <div className={leftSectionClass}>
        {getGenderIcon}
        <p className="text-lg font-bold text-primary">{actorTypeLabel}</p>
      </div>
      <div className="mt-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-primary">
          <Search className="h-4 w-4 shrink-0" />
          {t('ACTOR_FORM.SEARCH_BAR.LABELS.SEARCH_EXISTING_TITLE')}
        </div>
        <PersonSearchBar
          onSelect={handleSelectNaturalPerson}
          withNaturalPerson
          withLegalEntity={false}
          excludeFilter={excludeFilter}
          size="lg"
          className="w-full"
        />
        <p className="mt-2 text-xs text-primary/70">
          {t('ACTOR_FORM.SEARCH_BAR.LABELS.SEARCH_EXISTING_HINT')}
        </p>
      </div>
    </div>
  );
};
